// The sticky identity band. Answers "who is this and where are they" without
// the clinician scrolling, on every section of the workspace.
//
// Compact by design: this is the one element always on screen during a ward
// round on a tablet, so it stays two lines on a phone and one dense row on a
// desktop. Anything that is not identity, location, or a safety flag belongs
// in the snapshot below it, not here.
import { useCallback, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { cn } from '@/lib/cn';
import type { CaseSheet, CaseSheetDeficiency, CodeStatusValue } from './types';
import { fmtDateTime, fmtStay } from './ui';
import { GenerateCaseSheetDialog } from './GenerateCaseSheetDialog';
import { useMrdChecklist } from './hooks';
import { deficiencyFocus, sourceFocus, type FocusRequest } from './caseSheetTabs';
import { patientDisplayName } from '@/lib/patientName';

const CODE_STATUS_LABEL: Record<CodeStatusValue, string> = {
  full_code: 'Full code', dnar: 'DNAR', dni: 'DNI', comfort_care: 'Comfort care',
};

/**
 * Wireframe header chip: "MRD checklist: N gaps". Lists what a Final copy
 * would flag right now (the mrd_legal profile's thresholds — the strictest),
 * blocking first; an item whose fix lives in a case-sheet section opens it.
 * Absent until the checklist loads, and absent on error — it is a prompt,
 * never a gate (the Generate dialog re-checks on its own).
 */
function MrdChecklistChip({ admissionId, onOpenFocus }: { admissionId: number; onOpenFocus?: (req: FocusRequest) => void }) {
  const { data } = useMrdChecklist(admissionId);
  // Picking an item moves focus to the field that fixes it — so the menu must
  // not hand focus back to its trigger on close, which Radix does by default
  // and which would land after (and undo) the jump.
  const navigating = useRef(false);
  if (!data) return null;
  const blocking = data.deficiencies.blocking;
  // The same gap can be reported as a warning and as blocking (a missing
  // registration number) — count it once, as blocking.
  const warnings = data.deficiencies.warnings.filter(w => !blocking.some(b => b.key === w.key));
  const total = blocking.length + warnings.length;
  if (total === 0) return <Badge variant="success">MRD checklist: complete</Badge>;

  const item = (d: CaseSheetDeficiency, i: number) => {
    const focus = deficiencyFocus(d.key);
    return (
      <DropdownMenuItem
        key={`${d.key}-${i}`}
        disabled={!focus || !onOpenFocus}
        onSelect={() => { if (focus && onOpenFocus) { navigating.current = true; onOpenFocus(focus); } }}
        className="items-start whitespace-normal text-[12.5px] leading-snug pointer-coarse:py-3 data-[disabled]:opacity-100"
      >
        <span className="min-w-0 flex-1">{d.message}</span>
        {focus && <span aria-hidden className="shrink-0 text-ink-muted">›</span>}
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium pointer-coarse:min-h-9',
            'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-teal',
            blocking.length ? 'bg-danger-soft text-danger-ink' : 'bg-warn-soft text-warn-ink',
          )}
        >
          MRD checklist: {total} gap{total > 1 ? 's' : ''}
          <ChevronDown aria-hidden className="size-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" collisionPadding={8} className="w-[min(22rem,calc(100vw-16px))]"
        onCloseAutoFocus={(e) => { if (navigating.current) { e.preventDefault(); navigating.current = false; } }}>
        {blocking.length > 0 && (
          <>
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-danger-ink">Blocking for a Final copy</DropdownMenuLabel>
            {blocking.map(item)}
          </>
        )}
        {blocking.length > 0 && warnings.length > 0 && <DropdownMenuSeparator />}
        {warnings.length > 0 && (
          <>
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-ink-muted">Warnings</DropdownMenuLabel>
            {warnings.map(item)}
          </>
        )}
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-[11.5px] text-ink-muted">
          {blocking.length ? 'A Final copy needs these fixed, or an override reason.' : 'Only warnings — a Final copy can be generated.'}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** `top: -20px` (the base case, `offsetPx` omitted/0) is the real fix for
 * the gap-above-the-sticky-header bug — see ClaimHeader.tsx's own header
 * comment for the full mechanism, verified live against the DOM: a
 * `position: sticky` child's stuck offset is resolved against its scroll
 * container's PADDING edge (App.tsx's shared page wrapper puts `padding:
 * 20px 24px` directly on the scrolling element), and a negative `margin`
 * does nothing to move that — only `top` does. `offsetPx` lets a caller that
 * renders its own sticky toolbar directly above this header (IPModule.tsx's
 * "← Back to IP / Wards" row) push the stuck point down by that toolbar's
 * real height instead of overlapping it — set via a CSS custom property
 * through a ref (not a `style` prop; `features/**` is lint-gated against new
 * inline styles) the same way `features/emergency/ui.tsx`'s `useLevelVar`
 * does for a runtime-computed value. */
function useStickyTopVar(offsetPx: number) {
  return useCallback((el: HTMLElement | null) => {
    if (!el) return;
    el.style.setProperty('--sticky-top', `${offsetPx - 20}px`);
  }, [offsetPx]);
}

export function PatientHeader({ data, onClose, onOpenFocus, stickyOffsetPx = 0 }: {
  data: CaseSheet; onClose?: () => void; onOpenFocus?: (req: FocusRequest) => void; stickyOffsetPx?: number;
}) {
  const { patient, admission, allergies, allergy_status, alerts } = data;
  const redCount = alerts.filter(a => a.severity === 'red').length;
  const amberCount = alerts.filter(a => a.severity === 'amber').length;
  const stickyTopRef = useStickyTopVar(stickyOffsetPx);

  // §9.1: one dialog (profile/purpose/recipient, Preview, Generate) replaces
  // the old one-click .docx button — no module gate here beyond what already
  // got the clinician onto this screen, since generating is itself gated per
  // profile inside the dialog/route (ip:case-sheet-print vs -release).
  const [generateOpen, setGenerateOpen] = useState(false);

  // `-mt-5 -mx-6` keep the pre-scroll (static) position matching the stuck
  // one exactly, so there's no visual jump the instant scrolling starts.
  // Previously this only bled horizontally (`-mx-4`/`sm:-mx-5`, itself short
  // of the real 24px) and never vertically at all — why this had the same
  // gap-above-the-header bug the Claim workspace did.
  return (
    // Below 640px (App.tsx's phone breakpoint, where its page padding drops
    // to 14px) the header is static and bleeds 14px: pinned, its two-to-four
    // wrapped rows covered a third of a phone screen on top of IPModule's
    // own sticky toolbar. That toolbar stays pinned and is enough to navigate.
    <header ref={stickyTopRef} data-cs-header className="sticky top-[var(--sticky-top,-20px)] z-20 -mx-6 -mt-5 border-b border-border bg-card/95 px-6 pt-5 pb-2.5 backdrop-blur max-sm:static max-sm:-mx-3.5 max-sm:-mt-3.5 max-sm:px-3.5 max-sm:pt-3.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        {/* A real basis, not flex-1's zero: in a wrapping row a zero-basis column
            shrinks to one word per line instead of letting the badges wrap below. */}
        <div className="min-w-0 flex-1 basis-[18rem]">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="truncate text-[15px] font-semibold">{patientDisplayName(patient)}</span>
            <span className="text-xs text-ink-muted">
              {patient.age != null ? `${patient.age}y` : '—'} · {patient.gender}
              {patient.blood_group ? ` · ${patient.blood_group}` : ''}
            </span>
            <span className="text-xs text-ink-muted">UHID {patient.uhid}</span>
            {admission.ip_number && <span className="text-xs font-semibold">{admission.ip_number}</span>}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-ink-muted">
            <span>{admission.ward_name} · Bed {admission.bed_number}</span>
            <span>Day {admission.length_of_stay_days + 1} · {fmtStay(admission.length_of_stay_hours)}</span>
            <span>Admitted {fmtDateTime(admission.admitted_at)}</span>
            {(admission.consultant_name || admission.admitting_doctor) && (
              <span>Under {admission.consultant_name || admission.admitting_doctor}</span>
            )}
            {admission.transfer_count > 0 && (
              <span>{admission.transfer_count} transfer{admission.transfer_count > 1 ? 's' : ''}</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {admission.status === 'discharged' && <Badge variant="neutral">Discharged — read only</Badge>}
          {patient.identity_status && patient.identity_status !== 'verified' && (
            <Badge variant="danger" title="Admitted from the ED without a verified identity — resolve it from Emergency → the encounter → Resolve identity">
              Identity {patient.identity_status}
            </Badge>
          )}

          {/* Allergy state is three-valued and each is shown differently.
              "Not reviewed" is a warning, not an absence: nobody having asked
              is not the same as there being nothing to find. */}
          {allergy_status === 'recorded' && (
            <Badge variant="danger" title={allergies.map(a => a.substance).join(', ')}>
              Allergy: {allergies.slice(0, 2).map(a => a.substance).join(', ')}
              {allergies.length > 2 ? ` +${allergies.length - 2}` : ''}
            </Badge>
          )}
          {allergy_status === 'reviewed-none' && <Badge variant="success">No known allergies</Badge>}
          {allergy_status === 'not-reviewed' && <Badge variant="warn">Allergies not reviewed</Badge>}

          {redCount > 0 && <Badge variant="danger">{redCount} critical</Badge>}
          {amberCount > 0 && <Badge variant="warn">{amberCount} to review</Badge>}

          {/* Code status only when an order states it — never a default
              "Full code" nobody wrote. Any limitation of treatment is red. */}
          {admission.code_status && (
            <button
              type="button"
              onClick={() => onOpenFocus?.(sourceFocus(admission.code_status!.source) ?? { tab: 'orders', targets: ['clinical_order'] })}
              title={`Code status ordered ${fmtDateTime(admission.code_status.ordered_at)} — open Orders`}
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-semibold pointer-coarse:min-h-9',
                'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-teal',
                admission.code_status.value === 'full_code' ? 'bg-muted text-muted-foreground' : 'bg-danger-soft text-danger-ink',
              )}
            >
              {CODE_STATUS_LABEL[admission.code_status.value] ?? admission.code_status.value}
            </button>
          )}

          <MrdChecklistChip admissionId={admission.id} onOpenFocus={onOpenFocus} />

          <Button variant="ghost" size="sm" onClick={() => setGenerateOpen(true)}>
            📄 Generate Case Sheet…
          </Button>

          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          )}
        </div>
      </div>

      <GenerateCaseSheetDialog
        admissionId={admission.id}
        uhid={patient.uhid}
        open={generateOpen}
        onOpenChange={setGenerateOpen}
      />
    </header>
  );
}
